import Link from 'next/link';
import { forwardRef, type AnchorHTMLAttributes, type ReactNode, type Ref } from 'react';

/**
 * Universal portal link. Use it everywhere a block or shell emits an
 * {@code <a>} — it routes the href to the correct primitive:
 *
 * <ul>
 *   <li><b>Internal</b> ({@code /courses/123}, etc.) → {@code next/link},
 *       which auto-prepends the configured {@code basePath} and gives
 *       client-side navigation + prefetch.</li>
 *   <li><b>External</b> ({@code http(s):}, {@code mailto:}, {@code tel:},
 *       {@code sms:}) → plain {@code <a>} with sensible {@code target} /
 *       {@code rel} defaults for {@code http(s):}.</li>
 *   <li><b>Anchor-only</b> ({@code #frag}, missing/empty href, no-op
 *       {@code #}) → plain {@code <a>} so in-page anchors and "no link
 *       yet" placeholders don't accidentally trigger a route push.</li>
 * </ul>
 *
 * <p>Why this matters: when the host mounts the app under a
 * {@code basePath} (e.g. {@code /portal}), plain {@code <a href="/x">}
 * produces broken {@code /x} URLs instead of {@code /portal/x}. Routing
 * everything through {@code next/link} lets Next.js handle the prefix
 * exactly once, at the framework boundary.
 *
 * <p>Forwards refs to the underlying anchor element so callers that
 * need {@code getBoundingClientRect()} / {@code focus()} (e.g. the
 * MegaMenu trigger) keep working.
 */
export interface PortalLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string | undefined | null;
  children?: ReactNode;
}

const EXTERNAL_PROTO = /^(https?:|mailto:|tel:|sms:)/i;

function isExternal(href: string): boolean {
  return EXTERNAL_PROTO.test(href);
}

function isAnchorOnly(href: string): boolean {
  return href === '' || href === '#' || href.startsWith('#');
}

export const PortalLink = forwardRef<HTMLAnchorElement, PortalLinkProps>(function PortalLink(
  { href, children, ...rest },
  ref: Ref<HTMLAnchorElement>,
) {
  const target = (href ?? '#').toString();

  if (isAnchorOnly(target)) {
    return (
      <a ref={ref} href={target} {...rest}>
        {children}
      </a>
    );
  }

  if (isExternal(target)) {
    const isHttp = /^https?:/i.test(target);
    const extraAttrs =
      isHttp && rest.target === undefined
        ? { target: '_blank', rel: rest.rel ?? 'noopener noreferrer' }
        : {};
    return (
      <a ref={ref} href={target} {...extraAttrs} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link ref={ref} href={target} {...rest}>
      {children}
    </Link>
  );
});
